import { motion } from 'framer-motion';
import { SectionShell } from './SectionShell';
import { PRODUCT_PILLARS, type ProductPillar } from './product-content';

/**
 * The Product — what Asset Hound is, in five pillars.
 *
 * No product screens by design — this is investor positioning, not a
 * feature tour. Each pillar lands one capability with a precise verb,
 * tying back to the regulated-industry vocabulary (statutory output,
 * AI-as-advisor, immutable audit trail).
 */

export function TheProduct() {
  return (
    <SectionShell
      id="product"
      index={3}
      density="open"
      eyebrow="The product"
      title={
        <>
          One workflow.{' '}
          <span className="text-brand-bright">Six case types.</span> AI
          throughout
        </>
      }
      intro="Asset Hound is the AI workflow platform for UK insolvency practitioners. One unified, audited system with AI at its core, built for the regulator, not just the user"
    >
      <div className="pitch-strip grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-border bg-border-subtle md:grid-cols-2 lg:grid-cols-3">
        {PRODUCT_PILLARS.map((p, i) => (
          <PillarCell key={p.id} pillar={p} index={i} />
        ))}
      </div>
    </SectionShell>
  );
}

function PillarCell({
  pillar,
  index,
}: {
  pillar: ProductPillar;
  index: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1],
        delay: 0.05 + index * 0.06,
      }}
      className="pitch-cell pitch-cell-interactive flex flex-col gap-4 p-7 sm:p-8"
    >
      <span className="text-[11px] font-medium uppercase tabular-nums tracking-[0.22em] text-brand-bright">
        {pillar.num}
      </span>
      <h3 className="text-balance text-[20px] font-semibold leading-snug text-ink-strong sm:text-[22px]">
        {pillar.title}
      </h3>
      <p className="text-[14px] leading-relaxed text-ink-muted">
        {pillar.body}
      </p>
    </motion.div>
  );
}
